import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, FileText, Info, Stethoscope, Handshake, CheckCircle2, ShieldOff, Send, Copyright, ExternalLink, Server, Scale, Lock, RefreshCw, Phone, Clock } from "lucide-react";
import "@/App.css";

const WebsiteTerms = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    navigate("/", { state: { scrollTo: "contact" } });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === "function") action();
  };

  return (
    <div className="privacy-page" data-testid="terms-page">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <span className="logo-text">Precision Prescribing</span>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="nav-links">
            <button onClick={() => navigate("/clinical-areas")} className="nav-link" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate("/dispensing")} className="nav-link" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => navigate("/#how-it-works")} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => navigate("/#about")} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={scrollToContact} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>
          <div className={`mobile-nav ${mobileMenuOpen ? "mobile-nav-open" : ""}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate("/clinical-areas"))} className="mobile-nav-link" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/dispensing"))} className="mobile-nav-link" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#how-it-works"))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#about"))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(scrollToContact)} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="privacy-hero" data-testid="terms-hero">
        <div className="privacy-hero-inner">
          <span className="privacy-eyebrow" data-testid="terms-eyebrow">Legal</span>
          <h1 className="privacy-title" data-testid="terms-title">Website Terms of Use</h1>
          <p className="privacy-subtitle" data-testid="terms-subtitle">
            The terms that apply when you access or use the Precision Prescribing website.
          </p>
          <div className="privacy-meta" data-testid="terms-meta">
            <span className="privacy-meta-item"><Clock size={16} /> Last updated: 15 July 2026</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="privacy-layout privacy-layout-single" data-testid="terms-layout">
        <article className="privacy-article" data-testid="terms-article">

          {/* 1. Introduction */}
          <section className="privacy-block" data-testid="terms-section-introduction">
            <h2><FileText size={22} className="privacy-h2-icon" /> 1. Introduction</h2>
            <p>These Website Terms of Use explain the terms that apply when you access or use the Precision Prescribing website.</p>
            <div className="privacy-callout" data-testid="terms-entity-card">
              <FileText size={24} className="privacy-callout-icon" />
              <div>
                <p className="privacy-callout-title">Precision Prescribing</p>
                <p className="privacy-callout-sub">A trading name of PHARMKG LTD</p>
              </div>
            </div>
            <p>By using this website, you agree to use it responsibly and in accordance with these terms.</p>
          </section>

          {/* 2. About This Website */}
          <section className="privacy-block" data-testid="terms-section-about">
            <h2><Info size={22} className="privacy-h2-icon" /> 2. About This Website</h2>
            <p>This website provides general information about Precision Prescribing&rsquo;s B2B pharmacist-led prescribing support services.</p>
            <p>Precision Prescribing provides prescribing capacity and operational support to healthcare organisations, online clinics, pharmacies, digital health providers and other regulated healthcare providers.</p>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>This website is intended for business and professional users. It is not intended to provide patient-facing healthcare services.</p>
            </div>
          </section>

          {/* 3. Not Medical Advice */}
          <section className="privacy-block" data-testid="terms-section-not-medical">
            <h2><Stethoscope size={22} className="privacy-h2-icon" /> 3. Not Medical Advice</h2>
            <p>The information on this website is provided for general business information only.</p>
            <p>Nothing on this website should be treated as:</p>
            <ul className="privacy-list-grid">
              <li>Medical advice</li>
              <li>Patient advice</li>
              <li>Clinical guidance for individual patients</li>
              <li>A diagnosis</li>
              <li>A prescription</li>
              <li>An offer to prescribe</li>
              <li>An offer to dispense medication</li>
              <li>A replacement for professional medical advice</li>
            </ul>
            <div className="privacy-info-card privacy-info-card-warn">
              <p style={{ marginBottom: 0 }}>Patients should contact their own healthcare professional, GP, pharmacist, prescriber or emergency services where appropriate.</p>
            </div>
          </section>

          {/* 4. Services Are Subject to Agreement */}
          <section className="privacy-block" data-testid="terms-section-agreement">
            <h2><Handshake size={22} className="privacy-h2-icon" /> 4. Services Are Subject to Agreement</h2>
            <p>Information on this website does not create a contract between you and Precision Prescribing.</p>
            <p>Any services provided by Precision Prescribing are subject to separate discussion, due diligence, onboarding, agreed scope of work, commercial terms, data protection arrangements, clinical governance requirements and written agreement.</p>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>We may decline to provide services where we consider that the service model, governance arrangements, clinical pathway, operational requirements or commercial terms are not appropriate.</p>
            </div>
          </section>

          {/* 5. Accuracy of Website Information */}
          <section className="privacy-block" data-testid="terms-section-accuracy">
            <h2><CheckCircle2 size={22} className="privacy-h2-icon" /> 5. Accuracy of Website Information</h2>
            <p>We aim to keep the information on this website accurate and up to date.</p>
            <p>However, we do not guarantee that the website content will always be complete, current or free from errors.</p>
            <p>We may update, amend, remove or replace website content at any time.</p>
          </section>

          {/* 6. Use of the Website */}
          <section className="privacy-block" data-testid="terms-section-use">
            <h2><ShieldOff size={22} className="privacy-h2-icon" /> 6. Use of the Website</h2>
            <p>You must not misuse this website.</p>
            <p>You must not:</p>
            <ul>
              <li>Use the website unlawfully</li>
              <li>Attempt to gain unauthorised access to the website, server or related systems</li>
              <li>Submit false, misleading or malicious information through the contact form</li>
              <li>Upload harmful code, malware or viruses</li>
              <li>Interfere with the website&rsquo;s operation or security</li>
              <li>Copy website content for unauthorised commercial use</li>
              <li>Use the website in a way that may damage Precision Prescribing, PHARMKG LTD or any third party</li>
            </ul>
          </section>

          {/* 7. Enquiry Forms */}
          <section className="privacy-block" data-testid="terms-section-enquiries">
            <h2><Send size={22} className="privacy-h2-icon" /> 7. Enquiry Forms</h2>
            <p>If you submit an enquiry through the website, you must ensure that the information you provide is accurate and appropriate.</p>
            <div className="privacy-info-card privacy-info-card-warn">
              <p style={{ marginBottom: 0 }}>Please do not submit patient-identifiable information, confidential clinical information or sensitive personal data through the website enquiry form unless specifically requested and appropriate.</p>
            </div>
            <p>We will use enquiry information to respond to your request and manage potential business discussions.</p>
            <p>Please see our <a href="/privacy-policy">Privacy Policy</a> for more information about how personal information is handled.</p>
          </section>

          {/* 8. Intellectual Property */}
          <section className="privacy-block" data-testid="terms-section-ip">
            <h2><Copyright size={22} className="privacy-h2-icon" /> 8. Intellectual Property</h2>
            <p>The content on this website, including text, branding, layout, graphics, design and other materials, is owned by or licensed to PHARMKG LTD trading as Precision Prescribing unless stated otherwise.</p>
            <p>You may view and use the website for normal business information purposes.</p>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p style={{ marginBottom: 0 }}>You must not copy, reproduce, distribute, modify or use website content for commercial purposes without permission.</p>
            </div>
          </section>

          {/* 9. External Links */}
          <section className="privacy-block" data-testid="terms-section-external">
            <h2><ExternalLink size={22} className="privacy-h2-icon" /> 9. External Links</h2>
            <p>This website may contain links to third-party websites or resources.</p>
            <p>We are not responsible for the content, availability, privacy practices or security of third-party websites.</p>
            <p>Accessing third-party links is at your own discretion.</p>
          </section>

          {/* 10. Website Availability */}
          <section className="privacy-block" data-testid="terms-section-availability">
            <h2><Server size={22} className="privacy-h2-icon" /> 10. Website Availability</h2>
            <p>We aim to keep the website available and functioning properly.</p>
            <p>However, we do not guarantee that the website will always be available, uninterrupted, secure or error-free.</p>
            <p>We may suspend, withdraw or change all or part of the website at any time.</p>
          </section>

          {/* 11. Limitation of Liability */}
          <section className="privacy-block" data-testid="terms-section-liability">
            <h2><Scale size={22} className="privacy-h2-icon" /> 11. Limitation of Liability</h2>
            <p>This website is provided for general information purposes.</p>
            <p>To the fullest extent permitted by law, PHARMKG LTD trading as Precision Prescribing accepts no liability for loss or damage arising from reliance on website information, website unavailability, technical issues, or use of linked third-party websites.</p>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p style={{ marginBottom: 0 }}><strong>Nothing in these terms excludes or limits liability where it would be unlawful to do so.</strong></p>
            </div>
          </section>

          {/* 12. Privacy and Cookies */}
          <section className="privacy-block" data-testid="terms-section-privacy">
            <h2><Lock size={22} className="privacy-h2-icon" /> 12. Privacy and Cookies</h2>
            <p>Use of this website is also subject to our:</p>
            <ul>
              <li><a href="/privacy-policy">Privacy Policy</a></li>
              <li><a href="/cookie-policy">Cookie Policy</a></li>
            </ul>
            <p>These explain how we handle personal information and how cookies or similar technologies may be used.</p>
          </section>

          {/* 13. Changes to These Terms */}
          <section className="privacy-block" data-testid="terms-section-changes">
            <h2><RefreshCw size={22} className="privacy-h2-icon" /> 13. Changes to These Terms</h2>
            <p>We may update these Website Terms of Use from time to time.</p>
            <p>Any updated version will be published on this page with a revised &ldquo;Last updated&rdquo; date.</p>
          </section>

          {/* 14. Contact */}
          <section className="privacy-block privacy-block-contact" data-testid="terms-section-contact">
            <h2><Phone size={22} className="privacy-h2-icon" /> 14. Contact</h2>
            <p>For questions about these Website Terms of Use, please contact:</p>

            <div className="privacy-contact-grid privacy-contact-grid-single">
              <div className="privacy-contact-card" data-testid="terms-contact-card">
                <div className="privacy-contact-badge">Website Terms Enquiries</div>
                <p className="privacy-contact-name">Precision Prescribing</p>
                <a href="mailto:hello@precisionpx.com" className="privacy-contact-email" data-testid="terms-contact-email">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-10 5L2 7"/>
                  </svg>
                  hello@precisionpx.com
                </a>
              </div>
            </div>
          </section>

        </article>
      </div>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebsiteTerms;
